import clsx from "clsx";
import { uniq } from "lodash";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";
import { number, object, oneOfType, shape, string } from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { updateOutlineSections } from "slices/proposalOutline/outlineSlice";
import {
  selectIsEditing,
  selectProposalOutlineSections,
  selectSelectedRowIds,
} from "slices/proposalOutline/selectors";

import classes from "./SectionLevelCell.module.scss";

const SectionLevelCell = ({ row }) => {
  const isEditing = useSelector(selectIsEditing);
  const selectedRowIds = useSelector(selectSelectedRowIds);
  const sections = useSelector(selectProposalOutlineSections);
  const dispatch = useDispatch();

  const { id: rowId, original } = row;
  const { level } = original;
  const isNextDisabled = level === 6;

  const levelCls = clsx(
    classes["section_level_cell__level"],
    classes[`section_level_cell__level--${level}`]
  );

  const onToggleIndentation = (indent) => (e) => {
    e.preventDefault();
    e.stopPropagation();

    const rowIds = uniq([...selectedRowIds, rowId]);
    const updated = sections.reduce((arr, section) => {
      if (!rowIds.includes(section.id)) {
        return arr.concat(section);
      }

      const initialLevel = section.level || 1;
      const levelBounds = indent ? 6 : 1;
      const newLevel = indent
        ? Math.min(initialLevel + 1, levelBounds)
        : Math.max(initialLevel - 1, levelBounds);

      return arr.concat({
        ...section,
        level: newLevel,
      });
    }, []);

    dispatch(updateOutlineSections(updated));
  };

  if (!isEditing) {
    return (
      <span
        className={clsx(
          levelCls,
          classes["section_level_cell__level--readonly"]
        )}
      >
        L{level}
      </span>
    );
  }

  return (
    <div className={classes["section_level_cell"]}>
      <button
        className={classes["section_level_cell__arrow"]}
        data-show-on-row-hover
        disabled={level === 1}
      >
        <ArrowLeftIcon
          data-testid="chevron-left-icon"
          onClick={onToggleIndentation(false)}
          size={16}
        />
      </button>

      <span className={levelCls}>L{level}</span>

      <button
        className={classes["section_level_cell__arrow"]}
        data-show-on-row-hover
        disabled={isNextDisabled}
      >
        <ArrowRightIcon onClick={onToggleIndentation(true)} size={16} />
      </button>
    </div>
  );
};

SectionLevelCell.propTypes = {
  row: shape({
    id: oneOfType([number, string]),
    original: object,
  }),
};

export default SectionLevelCell;
